import { Products } from "@/api/signup/types/CardModel";
import { ITimelineItem } from "../task/types";

export interface IApplicationState {
  jdeAccountSetupTask: ITimelineItem | null;
  cardAccountSetupTask: ITimelineItem | null;
  modifyAccountNumberTask: ITimelineItem | null;
  followUpPersonalGuaranteeTask: ITimelineItem | null;
  myApplicationsList: IApplicationsDto[];
  myApplicationsListTotal?: number;
  id?: number;
  isLoading: boolean;
  applicationsList: IApplicationsDto[];
  applicationsListTotal?: number;
  completedApplicationsList: IApplicationsDto[];
  completedApplicationsListTotal?: number;
  draftsList: IDraftsDto[];
  draftsListTotal?: number;
  applicationData?: IApplicationDto | null;
  applicationDataOriginal?: IApplicationDto | null;
  ifcsOverrideData?: IIfcsDetails | null;
  ifcsOverrideDataOriginal?: IIfcsDetails | null;
  requestForCallbackTask: any;
  physicalAddressLookup: IAddressLookup | null;
  physicalAddressLookupIsLoading: boolean;
  deliveryAddressLookup: IAddressLookup | null;
  deliveryAddressLookupIsLoading: boolean;
  creditOutcomeTask: any;
  showAssignToMeConfirmation: boolean;
  dataTables: {
    myApplications: IDataTable;
    allApplications: IDataTable;
    drafts: IDataTable;
    completed: IDataTable;
  };
  activeApplicationTab: any;
}

export enum IApplicationStatus {
  Started = 1,
  DroppedOut = 2,
  Referred = 3,
  AwaitingPersonalGuarantee = 4,
  PersonalGuaranteeFollowUp = 5,
  Completed = 6,
  // CompletedWithCards = 7, Shouldn't be used anymore
  Cancelled = 8,
  Declined = 9,
  Approved = 10,
  CallbackRequested = 11,
  Draft = 12,
  CardAccountSetupComplete = 13,
}

export type IApplicationStatuses =
  | "Started"
  | "AwaitingPersonalGuarantee"
  | "Failed"
  | "Completed"
  | "Cancelled";

export interface IApplicationsDto {
  id: number;
  fullName: string;
  email: string;
  externalId: string;
  businessName: string;
  status: IApplicationStatus;
  createdDate: Date;
  updatedDate: Date;
}

export interface IDraftsDto {
  id: number;
  fullName: string;
  email: string;
  draftId: string;
  externalId: string;
  businessName: string;
  status: IApplicationStatus;
  createdDate: Date;
  isExpired: boolean;
}

export interface IApplicationDto {
  id: number;
  externalId: string;
  fullName?: string;
  email?: string;
  receiveCorrespondence?: boolean;
  status: IApplicationStatuses;
  callbackDetails?: ICallbackDetails;
  cancellationReason?: string;
  submittedDate?: Date;
  ifcsAccountNumber?: string;
  jdeAccountNumber?: string;
  creditOutcome?: string;
  creditOutcomeReason?: string;
  creditDecisionChangeReason?: string;
  creditLimit?: number;
  businessDetails?: IBusinessDetails;
  loyaltyDetails?: ILoyaltyDetails;
  creditDetails?: ICreditDetails;
  finalDetails?: IFinalDetails;
  cardDetails?: ICardDetails;
}

export interface IBusinessDetails {
  businessType?: string;
  fuelCardBusinessUseOnly?: boolean;
  business?: {
    nzbn?: string;
    entityName?: string;
    entityTypeCode?: string;
    tradingNames?: string[];
    industryClassification?: [
      {
        uniqueIdentifier?: string;
        classificationCode?: string;
        classificationDescription?: string;
      }
    ];
    registrationDate?: Date;
    sourceRegisterUniqueIdentifier?: string;
  };
  businessName?: string;
  hasManualInputBusiness?: boolean;
  hasDifferentTradingName?: boolean;
  tradingName?: string;
  physicalAddress?: {
    dpid?: string;
    fullAddress?: string;
    sourceDesc?: string;
  };
  physicalAddressText?: string;
  hasManualPhysicalAddress?: boolean;
  fuelAmount?: number;
  businessNameComplete?: boolean;
  physicalAddressComplete?: boolean;
  sectionComplete?: boolean;
}

export interface ILoyaltyDetails {
  promoOfferId?: number;
  standardOfferId?: number;
  promoCode?: string;
  membershipNumber?: string;
  pointType?: string;
  flyBuysNumber?: number;
  airPointsNumber?: number;
  airPointsFirstName?: string;
  airPointsLastName?: string;
  airPointsValid?: boolean;
  promoCodeValid?: boolean;
  promoCodeComplete?: boolean;
  sectionComplete?: boolean;
  flyBuysValid?: boolean;
  membershipNumberRequired?: boolean;
}

export interface ICreditDetails {
  status?: string;
  isCompanyDirector?: boolean;
  readRepresentativeRequirements?: boolean;
  legalName?: string;
  gender?: string;
  dateOfBirth?: Date;
  driversLicenseNumber?: string;
  driversLicenseVersionNo?: string;
  agreedToCreditCheck?: boolean;
  agreedToPersonalGuarantee?: boolean;
  personalGuaranteeEmail?: string;
  personalGuaranteeFileLocation: string;
  personalGuaranteeObtainedByEmailOrPost?: boolean;
  creditCheckPassed?: boolean;
  creditCheckSufficientInfo?: boolean;
  legalNameComplete?: boolean;
  dateOfBirthComplete?: boolean;
  driversLicenseComplete?: boolean;
  residentialAddressComplete?: boolean;
  sectionComplete?: boolean;
  residentialAddress?: {
    dpid?: string;
    fullAddress?: string;
    sourceDesc?: string;
  };
  residentialAddressText?: string;
  hasManualResidentialAddress?: boolean;
}

export interface IFinalDetails {
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankId?: number;
  contactNumber?: string;
  contactNumberComplete?: boolean;
  deliveryAddress?: {
    dpid?: string;
    fullAddress?: string;
    sourceDesc?: string;
  };
  deliveryAddressComplete?: boolean;
  deliveryAddressText?: string;
  hasAcceptedTermsAndConditions?: boolean;
  hasBankSigningAuthority?: boolean;
  hasManualDeliveryAddress?: boolean;
  invoiceEmail?: string;
  invoiceEmailComplete?: boolean;
  paymentPlan?: string;
  invoiceDate?: string;
  sectionComplete?: boolean;
}

export interface IIfcsDetails {
  physicalAddress?: IAddress;
  postalAddress?: IAddress;
}

export interface ICallbackDetails {
  contactNumber: number;
  email: string;
  fullName: string;
}

export interface IAddressLookup {
  dpid: number;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: any;
  addressLine5: any;
  postcode: string;
  sourceDesc: string;
  deliverable: string;
  physical: string;
  unitType: any;
  unitValue: any;
  floor: any;
  streetNumber: number;
  streetAlpha: string;
  roadName: string;
  roadTypeName: string;
  roadSuffixName: string;
  suburb: string;
  ruralDelivery: any;
  lobby: any;
  cityTown: string;
  mailTown: any;
  boxBagNumber: any;
  boxBagType: any;
}

export interface ICardDetails {
  activeCardIndex?: number;
  cards?: ICard[];
  deliveryAddress?: {
    dpid?: string;
    fullAddress?: string;
    sourceDesc?: string;
  };
  deliveryAddressText?: string;
  hasManualDeliveryAddress?: boolean;
}

export interface ICard {
  cardType?: string;
  disabled?: boolean;
  driver?: {
    name?: string;
  };
  fuelTypes?: {
    all?: boolean;
    diesel?: boolean;
    unleaded91?: boolean;
    zx?: boolean;
    zdec?: boolean;
  };
  limits?: {
    daily?: number;
    monthly?: number;
  };
  products?: Products;
  vehicle?: {
    registration?: string;
    make?: string;
    model?: string;
  };
}

interface IDataTable {
  statusFilters: any[] | null;
  pagination: {
    descending?: boolean;
    page?: number;
    rowsPerPage?: number; // -1 for All"
    sortBy?: string;
    totalItems?: number;
  };
}

interface IAddress {
  line1: string;
  line2?: string;
  line3?: string;
  postCode: string;
  country: string;
}
